/**
 * Cache identity for LLM profiles.
 *
 * Typed factories rather than inline arrays so the query and every
 * invalidation that follows a mutation cannot drift apart (constitution
 * §2.6). Organization-wide (spec 080) — a profile no longer belongs to a
 * workspace. `secretKeys`/`secretKeyWorkspaces` are gone (spec
 * 084-ai-providers-admin) — credential resolution no longer happens on this
 * entity at all.
 */
export const llmProfileKeys = {
  all: () => ['llm-profiles'] as const,
  list: () => ['llm-profiles', 'list'] as const,
  /**
   * Every profile with its connection, spend and usage — the admin grid's one
   * read (spec 136 FR-008).
   *
   * Org-admin-only and **all-time**, so it takes no arguments: the endpoint
   * accepts no query parameters at all and the figures are organization-wide by
   * design. Do not add a `period` argument by analogy with
   * `organizationKeys.usage(period)` — there is no period to narrow.
   *
   * Its own key rather than a widening of `list()`: different endpoint,
   * different audience, different shape, and `list()` is read by the workflow
   * builder's step editor, which must stay unaffected. Nested under `all()`, so
   * every existing mutation's `invalidateQueries({ queryKey: all() })` already
   * covers it with no edit — but that is invalidation only. The *optimistic*
   * `setQueryData` in `useSetDefaultLlmProfile` has to name this key explicitly
   * (spec 136 FR-018b).
   */
  adminOverview: () => ['llm-profiles', 'admin-overview'] as const,
  /**
   * The organization's current price per model, org-wide (spec 092 FR-007).
   *
   * Not period-scoped, unlike `organizationKeys.usage(period)`: this read answers
   * "what does this model cost right now", not "what did it cost during X".
   */
  modelPrices: () => ['llm-profiles', 'model-prices'] as const,
  /**
   * How much of the current period ran unpriced, per model (spec 092 follow-up).
   *
   * Current-period only, unlike `modelPrices()` above — this answers "how much of
   * *this period* is unpriced", not "does this model have a price right now".
   */
  unpricedModelRuns: () => ['llm-profiles', 'unpriced-model-runs'] as const,
};
